import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import type { IconProps } from './icon-props.js';
import { DEFAULT_SIZE } from './icon-props.js';

/**
 * Surface publique des icones @taki/ui.
 *
 * Le gros du set (les 499 composants nommes issus de Streamline Sharp Remix) est
 * GENERE par `tools/gen-icons.mjs` sous `generated/`, plus les 10 alias
 * semantiques historiques. Ce fichier n'ajoute que ce qui reste ecrit a la main :
 * le type IconProps, le helper de trait `createLineIcon`, et les glyphes "line"
 * (plus, chevrons, fleches, check, close, sparkle Ask AI) qui ne viennent pas
 * du set remix.
 *
 * Le REGISTRE complet ({ name: innerSvg } + <Icon name=".."/>) vit dans un ENTRY
 * POINT SEPARE ('@taki/ui/icon-registry'), a n'importer que la ou on a besoin de
 * tout le set (grille du wizard) pour ne pas casser le tree-shaking.
 *
 * @example
 * <IconModule className="text-ink-600" size={20} />
 */
export type { IconProps } from './icon-props.js';

export * from './generated/index.js';
export * from './generated/aliases.js';

/**
 * Fabrique une icone "line" (fill none, stroke currentColor). Reservee aux
 * glyphes de trait du design (plus, chevrons, check, close, sparkle Ask AI).
 */
function createLineIcon(
  displayName: string,
  body: ReactNode,
  strokeWidth: number,
  linejoin: 'round' | 'miter' = 'round',
) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
    { size = DEFAULT_SIZE, ...props },
    ref,
  ) {
    // size est en px de reference (maquette 1920) : rendu en rem pour suivre
    // l'echelle globale (tokens.css, regle racine).
    return (
      <svg
        ref={ref}
        width={`${size / 16}rem`}
        height={`${size / 16}rem`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin={linejoin}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        {...props}
      >
        {body}
      </svg>
    );
  });
  Icon.displayName = displayName;
  return Icon;
}

/* -------------------------------------------------------------------------- */
/* Icones line (trait) - reprises a l'identique du brand book Taki             */
/* -------------------------------------------------------------------------- */

/** Ask AI / sparkle (magic-wand-2, rendu en trait dans le CTA "Ask AI"). */
export const IconSparkle = createLineIcon(
  'IconSparkle',
  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />,
  1.8,
);

/** Plus (ajouter). */
export const IconPlus = createLineIcon(
  'IconPlus',
  <>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </>,
  2.8,
);

/** Chevron bas. */
export const IconChevronDown = createLineIcon(
  'IconChevronDown',
  <path d="m6 9 6 6 6-6" />,
  1.9,
);

/** Chevron droite. */
export const IconChevronRight = createLineIcon(
  'IconChevronRight',
  <path d="m9 18 6-6-6-6" />,
  1.8,
);

/**
 * Fleche PLEINE gauche (fut + tete), pas un chevron : le "precedent" du curseur
 * de fiche. Traits du prototype gabarit v2 (inventaire-v21-fiche-5.html).
 */
export const IconArrowLeft = createLineIcon(
  'IconArrowLeft',
  <>
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </>,
  1.8,
);

/** Fleche pleine droite : le "suivant" du curseur de fiche. */
export const IconArrowRight = createLineIcon(
  'IconArrowRight',
  <>
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </>,
  1.8,
);

/** Check (validation). */
export const IconCheck = createLineIcon('IconCheck', <path d="M20 6 9 17l-5-5" />, 3);

/** Close (fermer). */
export const IconClose = createLineIcon(
  'IconClose',
  <>
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </>,
  3,
);

/** Download (compteur d'installations de l'App Store). */
export const IconDownload = createLineIcon(
  'IconDownload',
  <>
    <path d="M12 3v11" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </>,
  2,
);
